import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { fetchPublicContent } from "../admin/api";
import * as defaults from "../mock";

/**
 * ContentContext supplies all editable site content.
 * It deep-merges DB content over mock.js defaults so missing fields fall back to defaults.
 */
const ContentContext = createContext(null);

const isPlainObject = (v) =>
  v !== null && typeof v === "object" && !Array.isArray(v);

function deepMerge(base, override) {
  if (override === undefined || override === null) return base;
  if (Array.isArray(override)) return override; // arrays are replaced wholesale
  if (isPlainObject(base) && isPlainObject(override)) {
    const out = { ...base };
    for (const k of Object.keys(override)) {
      out[k] = deepMerge(base[k], override[k]);
    }
    return out;
  }
  return override;
}

const defaultContent = {
  company: defaults.company,
  navLinks: defaults.navLinks,
  heroSlides: defaults.heroSlides,
  trustBadges: defaults.trustBadges,
  aboutSection: defaults.aboutSection,
  services: defaults.services,
  whyChooseUs: defaults.whyChooseUs,
  processSteps: defaults.processSteps,
  projects: defaults.projects,
  industries: defaults.industries,
  testimonials: defaults.testimonials,
  indianStates: defaults.indianStates,
  billRanges: defaults.billRanges,
  faqs: defaults.faqs,
  footerLinks: defaults.footerLinks,
  socials: defaults.socials,
  legalPages: defaults.legalPages,
  blogPosts: defaults.blogPosts,
};

export function ContentProvider({ children }) {
  const [content, setContent] = useState(defaultContent);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    try {
      const db = await fetchPublicContent();
      setContent(deepMerge(defaultContent, db || {}));
    } catch {
      setContent(defaultContent);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const value = useMemo(
    () => ({ content, loading, reload, defaults: defaultContent }),
    [content, loading, reload]
  );

  return (
    <ContentContext.Provider value={value}>
      {children}
    </ContentContext.Provider>
  );
}

export const useContent = () => {
  const ctx = useContext(ContentContext);
  if (!ctx) throw new Error("useContent must be used inside ContentProvider");
  return ctx;
};
